import { useState } from "react";
import { useAxios } from "../../../utils/useAxios";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";

/**
 * @typedef {Object} UpdateClassroomData
 * @property {number} [teacher_id] - Teacher ID (optional, to reassign classroom)
 * @property {string} [name] - Classroom name (optional)
 * @property {string|null} [description] - Classroom description (nullable)
 * @property {string|null} [start_date] - Start date in YYYY-MM-DD format (nullable)
 * @property {string|null} [end_date] - End date in YYYY-MM-DD format (nullable)
 * @property {string} [status] - Classroom status ('0' or '1') (optional)
 */

/**
 * @typedef {Object} UpdateClassroomParams
 * @property {number} classroomId - The classroom ID to update
 * @property {UpdateClassroomData} classroomData - The data to update
 */

/**
 * Custom hook to update classroom details
 */
export const useUpdateClassroom = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const { axiosInstance } = useAxios();

  /**
   * Updates a classroom
   * @param {UpdateClassroomParams} params - The update parameters
   * @returns {Promise<Object|null>} - Returns the API response or null if failed
   */
  const updateClassroom = async ({ classroomId, classroomData }) => {
    setLoading(true);
    setError("");
    setData(null);

    try {
      const response = await axiosInstance.put(
        `${TENANT_DOMAIN}/classroom/update/${classroomId}`,
        classroomData,
      );

      setData(response?.data?.data || null);
      return response.data;
    } catch (err) {
      let firstErrorMessage = "";
      if (err?.response?.data?.errors) {
        firstErrorMessage = Object.values(err?.response?.data?.errors)[0][0];
      }

      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(
          firstErrorMessage ||
            err?.response?.data?.message ||
            ERROR_MESSAGES.UNKNOWN,
        );
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateClassroom,
    loading,
    error,
    data,
  };
};

export default useUpdateClassroom;
